import { useConfirmedMediaDeletion } from '../../hooks/useConfirmedMediaDeletion'
import { useExecutionText } from '../../hooks/useExecutionText'
import { findScopedResult } from '../../model/results'
import './executionMedia.css'
import { ExecutionMediaSubtask } from '../../components/ExecutionMediaSubtask'
import React, { useEffect, useRef, useState } from 'react'
import { Avatar, Box, Flex, Text, Skeleton } from '@chakra-ui/core'
import { AppDispatch } from 'config/redux'
import colors from 'constants/colors'
import keys from 'constants/keys'
import { useMediaState } from 'hooks/useMediaState'
import { useDispatch, useSelector } from 'react-redux'
import { updateResults } from 'redux/actions/tasks_v2'
import MediaSlider from 'components/CommonComponents/MediaSlider'
import { ResultCommments } from 'components/TasksV2/CommentsSection/ResultCommments'
import { ImagePlus, Sparkles, MessageSquarePlus, Trash2, Video } from 'lucide-react'
import type { SubtaskWithUuid } from '../../model/media'
import { getMediaCid } from 'utils'
import { RootState } from 'constants/interfaces'
import { AI_USER_ID, createAIComment, deleteCommentsOfMedia } from 'redux/actions/comments'
import { captureEvent } from 'services/posthog'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import { callLLMInteraction } from 'redux/actions/promts'
import { parseLLMResponse, IParsedLLMResponse } from 'utils/parseLLMResponse'
import useAvatar from 'hooks/useAvatar'
import useLocaleText from 'components/useLocaleText'
import { isRtlSelector } from 'redux/selectors/general'
import { btnShimmerCss } from 'components/ui/Button/shimmer'
import { AiScoreTag } from 'components/TasksV2/UI/AiScoreTag'
import { useOptionalTaskOverviewContext } from '../../TaskOverviewStackProvider'
import { Button } from 'components/ui'
import { EmptyState } from 'components/ui/EmptyState'
import { IReviewFeedback } from '../../model/review'

// ─── types ────────────────────────────────────────────────────────────────────

export interface IMediaSubtaskProps {
  embedded?: boolean
  splitView?: boolean
  mediaFooter?: React.ReactNode
  mediaHeader?: React.ReactNode
  reviewFeedback?: IReviewFeedback | null
  images: string[]
  videos: string[]
  subtask: SubtaskWithUuid
  retail_id_context_id: string
  recurrence_ts: string | null
  resultContextId: string
  isReadMode: boolean
  mediaType: number
  onMediaChange?: (index: number) => void
  isShowSubtaskTitle?: boolean
  currentMediaIndex?: number
  tid: string
  isReworkLimitReached?: boolean
  targetParentCommentId?: string | null
  targetCommentId?: string | null
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const getScoreColor = (score: number) => {
  if (score >= 86) return '#22c55e'
  if (score >= 70) return '#f59e0b'
  return '#ef4444'
}

const shimmerKeyframes = `
  ${btnShimmerCss}
  @keyframes _ai_bar_slide {
    0%   { transform: translateX(-120%); }
    100% { transform: translateX(500%); }
  }
`

// ─── sub-components ───────────────────────────────────────────────────────────

interface IAIScoreCardProps {
  isAIProcessing: boolean
  displayAIScore: number | undefined
  aiAvatar: string
  elementsColor: string | undefined
  isRtl: boolean
}

const AIScoreCard: React.FC<IAIScoreCardProps> = ({
  isAIProcessing,
  displayAIScore,
  aiAvatar,
  elementsColor,
  isRtl,
}) => (
  <Box bg="white" mb="16px" p="16px" borderRadius="16px">
    <Flex alignItems="center" style={{ gap: '8px' }}>
      <Avatar src={aiAvatar} size="sm" />
      <Sparkles size={15} color={isAIProcessing ? elementsColor ?? '#6366f1' : getScoreColor(displayAIScore!)} />
      <Text fontWeight="700" fontSize="13px" color="#1a1a1a" flex={1}>
        AI Analysis
      </Text>
    </Flex>

    <Flex alignItems="center" justifyContent="space-between">
      <Box
        h="5px"
        borderRadius="4px"
        bg="#F1F5F9"
        overflow="hidden"
        w="80%"
        ml={isRtl ? '10px' : '40px'}
        mr={isRtl ? '40px' : '10px'}
      >
        {isAIProcessing ? (
          <Box
            h="100%"
            w="28%"
            borderRadius="4px"
            bg={elementsColor ?? '#6366f1'}
            style={{ animation: '_ai_bar_slide 1.3s ease-in-out infinite', opacity: 0.85 }}
          />
        ) : (
          <Box
            h="100%"
            borderRadius="4px"
            bg={getScoreColor(displayAIScore!)}
            style={{ width: `${displayAIScore}%`, transition: 'width 0.7s ease' }}
          />
        )}
      </Box>
      <Box style={{ visibility: isAIProcessing ? 'hidden' : 'visible' }}>
        <AiScoreTag scoreDeduction={100 - (displayAIScore ?? 0)} size="md" />
      </Box>
    </Flex>
  </Box>
)

interface IMediaOverlayButtonsProps {
  isImage: boolean
  isRtl: boolean
  isReadMode: boolean
  is_ai_enhanced: boolean | undefined
  isRetakeBlocked: boolean
  isInteractionBlocked: boolean
  sliderHovered: boolean
  mediaURLs: string[]
  retakeInputRef: React.RefObject<HTMLInputElement>
  handleUploadMedia: (filesOrEvent: File[] | React.ChangeEvent<HTMLInputElement>) => Promise<void>
  t_comment_on_image: string
  t_comment_on_video: string
  t_retake: string
  onComment: () => void
  onDelete: () => void
  deleteLabel: string
}

const MediaOverlayButtons: React.FC<IMediaOverlayButtonsProps> = ({
  isImage,
  isRtl,
  isReadMode,
  is_ai_enhanced,
  isRetakeBlocked,
  isInteractionBlocked,
  sliderHovered,
  mediaURLs,
  retakeInputRef,
  handleUploadMedia,
  t_comment_on_image,
  t_comment_on_video,
  t_retake,
  onComment,
  onDelete,
  deleteLabel,
}) => (
  <>
    {/* Comment button — top-left (RTL: top-right) */}
    {mediaURLs.length > 0 && (isImage ? sliderHovered : true) && (
      <Button
        pos="absolute"
        top="10px"
        left={isRtl ? 'auto' : '10px'}
        right={isRtl ? '10px' : 'auto'}
        zIndex={99998}
        display="flex"
        alignItems="center"
        style={{ gap: 5 }}
        bg="rgba(0,0,0,0.55)"
        px="10px"
        py="5px"
        h="auto"
        borderRadius="full"
        _hover={{ bg: 'rgba(0,0,0,0.75)' }}
        _focus={{ outline: 'none' }}
        onClick={onComment}
      >
        <MessageSquarePlus size={13} color="white" />
        <Text fontSize="12px" color="white" fontWeight="600">
          {isImage ? t_comment_on_image || 'Comment on image' : t_comment_on_video || 'Comment on video'}
        </Text>
      </Button>
    )}

    {/* AI flow: Retake button — bottom-left (RTL: bottom-right) */}
    {is_ai_enhanced && !isReadMode && !isRetakeBlocked && (
      <>
        <input
          type="file"
          accept={isImage ? 'image/*' : 'video/*'}
          multiple
          ref={retakeInputRef}
          style={{ display: 'none' }}
          onChange={handleUploadMedia as React.ChangeEventHandler<HTMLInputElement>}
        />
        <Button
          pos="absolute"
          bottom="10px"
          left={isRtl ? 'auto' : '10px'}
          right={isRtl ? '10px' : 'auto'}
          zIndex={99998}
          display="flex"
          alignItems="center"
          bg="white"
          px="10px"
          py="5px"
          h="auto"
          borderRadius="full"
          boxShadow="0px 2px 6px rgba(0,0,0,0.25)"
          _hover={{ bg: 'gray.100' }}
          _focus={{ outline: 'none' }}
          onClick={(e) => {
            e.stopPropagation()
            retakeInputRef.current?.click()
          }}
        >
          <Text fontSize="12px" color="black" fontWeight="600">
            {t_retake || 'Retake'}
          </Text>
        </Button>
      </>
    )}

    {/* Delete button — bottom-right */}
    {!isReadMode && !isInteractionBlocked && (
      <Button
        type="button"
        variant="icon"
        aria-label={deleteLabel}
        cursor="pointer"
        bottom="10px"
        right="10px"
        p={0}
        pos="absolute"
        zIndex={99999}
        bg="white"
        w="30px"
        height="30px"
        minW="unset"
        borderRadius="full"
        transition="0.2s ease-in-out"
        _hover={{ transform: 'scale(1.1)' }}
        _focus={{ outline: 'none' }}
        onClick={onDelete}
      >
        <Trash2 size={15} color="black" />
      </Button>
    )}
  </>
)

// ─── main component ───────────────────────────────────────────────────────────

const LegacyMediaSubtaskCompleting: React.FC<IMediaSubtaskProps> = ({
  images,
  videos,
  subtask,
  retail_id_context_id,
  recurrence_ts,
  onMediaChange,
  resultContextId,
  isReadMode: isReadOnlyContext,
  mediaType,
  splitView = false,
  mediaHeader,
  mediaFooter,
  isShowSubtaskTitle = false,
  currentMediaIndex = 0,
  tid,
  isReworkLimitReached = false,
  targetParentCommentId,
  targetCommentId,
}) => {
  const { st_id, is_ai_enhanced } = subtask
  const dispatch: AppDispatch = useDispatch()
  const { cdn, rid, retailUsersObject } = useSelector((state: RootState) => state.config.config)
  const theme = useSelector((state: RootState) => state.general.theme)
  const { allResults } = useSelector((state: RootState) => state.tasks_v2)
  const aiAvatar = useAvatar(AI_USER_ID)
  const isRtl = useSelector(isRtlSelector)

  const retakeInputRef = useRef<HTMLInputElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const mounted = useRef(true)
  const scope = JSON.stringify([tid, st_id, resultContextId, recurrence_ts])
  const activeScope = useRef(scope)
  const pendingUpload = useRef<{ scope: string; images: string[]; videos: string[]; committed?: boolean } | null>(null)
  activeScope.current = scope

  const [imageToComment, setImageToComment] = useState<string | undefined>(undefined)
  const [sliderHovered, setSliderHovered] = useState(false)
  const [isAIProcessing, setIsAIProcessing] = useState(false)
  const [isSavingMedia, setIsSavingMedia] = useState(false)
  const [uploadFailed, setUploadFailed] = useState(false)
  const [aiResult, setAiResult] = useState<IParsedLLMResponse | null>(null)

  const t_comment_on_image = useLocaleText('comment_on_image')
  const t_comment_on_video = useLocaleText('comment_on_video')
  const t_retake = useLocaleText('retake')
  const t_no_media = useLocaleText('nothing_found_massage')
  const t = useExecutionText()
  const deletion = useConfirmedMediaDeletion(JSON.stringify([tid, st_id, resultContextId, recurrence_ts]))

  // ── derived from Redux ──

  const currentResult = findScopedResult(allResults?.[tid], st_id, resultContextId, recurrence_ts)
  // Insights can supply a flattened gallery from several subtasks. Editing is only safe
  // when every displayed URL belongs to this concrete subtask/occurrence.
  const ownMedia = new Set([...(currentResult?.images ?? []), ...(currentResult?.videos ?? [])])
  const isAggregateGallery = [...images, ...videos].some((url) => !ownMedia.has(url))
  const isReadMode = isReadOnlyContext || isAggregateGallery
  const currentRating = currentResult?.rating ?? 0
  const storedReservations = currentResult?.reservations
  const displayAIScore =
    aiResult?.aiScore ?? (storedReservations ? 100 - storedReservations.score_deduction : undefined)
  const displayAIFeedback = aiResult?.feedbackText ?? storedReservations?.reasons?.[0]

  // ── media state ──

  const {
    uploadedImagesURL,
    uploadedVideosURL,
    setUploadedImagesURL,
    setUploadedVideosURL,
    handleMediaUploading,
    addMediaHandler,
    imageInputRef,
    loading,
    handleDeleteMedia,
  } = useMediaState({ images: images ?? [], videos: videos ?? [] })

  // Surface the whole in-flight sequence to the modal — upload AND the AI analysis that now follows it
  // automatically. The modal locks its pager, Back and Done for the duration, so the user cannot leave
  // or finish the subtask half-way through.
  const setIsSubtaskBusy = useOptionalTaskOverviewContext()?.setIsSubtaskBusy

  const originalMedia =
    pendingUpload.current?.scope === scope && !pendingUpload.current.committed ? pendingUpload.current : null
  const mediaURLs = originalMedia
    ? [...originalMedia.images, ...originalMedia.videos]
    : [...(uploadedImagesURL ?? []), ...(uploadedVideosURL ?? [])]
  const isImage = mediaType === keys.SUBTASK_TYPES.IMAGE_SUBTASK
  const Icon = isImage ? ImagePlus : Video

  // ── context helpers ──

  const contextIdRecurrenceTs = `${resultContextId}${recurrence_ts ? '_' + recurrence_ts : ''}`
  const commentOid = `${st_id}_${contextIdRecurrenceTs}`
  const resultLevel = Object.keys(retailUsersObject ?? {}).includes(resultContextId) ? 'user' : 'group'

  // ── interaction gates ──

  const isInteractionBlocked =
    isReadMode || isReworkLimitReached || isAIProcessing || loading || deletion.busy || isSavingMedia
  const isRetakeBlocked =
    isReadMode || isReworkLimitReached || isAIProcessing || loading || deletion.busy || isSavingMedia

  // ── scroll helper — used after upload, retake, and AI analysis ──

  const scrollToBottom = () => {
    if (splitView) return
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }, 150)
  }

  // ── handlers ──

  const handleUploadMedia = async (filesOrEvent: File[] | React.ChangeEvent<HTMLInputElement>) => {
    if (isInteractionBlocked || pendingUpload.current) return
    const input = Array.isArray(filesOrEvent) ? null : filesOrEvent.target
    const files = Array.isArray(filesOrEvent) ? filesOrEvent : Array.from(input?.files || [])
    // Cancelling the chooser must not replace an existing result or enter a busy state.
    if (!files.length) return

    const request = { scope, images: [...uploadedImagesURL], videos: [...uploadedVideosURL], committed: false }
    const isCurrent = () => mounted.current && activeScope.current === request.scope
    const isReplace = !!is_ai_enhanced && mediaURLs.length > 0
    const originalURLs = [...request.images, ...request.videos]
    pendingUpload.current = request
    setIsSavingMedia(true)
    setUploadFailed(false)
    let saved = false

    try {
      const data = await handleMediaUploading({ target: { files } } as any)
      if (!isCurrent()) return
      const uploadedBatch = isImage ? data?.uploadedImages : data?.uploadedVideos
      if (!uploadedBatch?.length || uploadedBatch.some((url) => !url)) throw new Error('No media uploaded')
      const newMedia = [...(isReplace ? [] : mediaURLs), ...uploadedBatch]
      saved = await dispatch(
        updateResults({
          [isImage ? 'images' : 'videos']: newMedia,
          tid,
          st_id,
          type: isImage ? keys.SUBTASK_TYPES.IMAGE_SUBTASK : keys.SUBTASK_TYPES.VIDEO_SUBTASKS,
          resultContextId,
          recurrence_ts,
          status: keys.TASK_STATUSES.DONE.value,
          rating: currentRating,
          optimistic: false,
          ...(isReplace ? { reservations: { reasons: [], score_deduction: 0 } } : {}),
        })
      )
      if (!saved) throw new Error('Could not save uploaded media')

      if (isReplace) {
        const hashes = originalURLs
          .filter((url) => !newMedia.includes(url))
          .map((url) => getMediaCid({ url, cdn, rid }))
          .filter((hash): hash is string => !!hash)
        if (hashes.length) {
          // Comment cleanup belongs to the captured result, even if its viewer has closed.
          void Promise.resolve()
            .then(() =>
              dispatch(
                deleteCommentsOfMedia(
                  hashes.map((hash) => `${commentOid}-${hash}`),
                  hashes,
                  hashes.map(() => commentOid)
                )
              )
            )
            .catch(console.error)
        }
      }
      if (!isCurrent()) return
      setUploadedImagesURL(isImage ? newMedia : [])
      setUploadedVideosURL(isImage ? [] : newMedia)
      setAiResult(null)
      request.committed = true

      if (is_ai_enhanced && !isReadMode && !isReworkLimitReached) {
        await handleAnalyzeAI(newMedia)
      } else {
        scrollToBottom()
      }
    } catch (error) {
      if (isCurrent()) setUploadFailed(true)
    } finally {
      if (isCurrent()) {
        if (!saved) {
          setUploadedImagesURL(request.images)
          setUploadedVideosURL(request.videos)
        }
      }
      if (pendingUpload.current === request) pendingUpload.current = null
      if (mounted.current) setIsSavingMedia(false)
      if (input) input.value = ''
      if (retakeInputRef.current) retakeInputRef.current.value = ''
      if (imageInputRef.current) imageInputRef.current.value = ''
    }
  }

  /**
   * `urls` is passed explicitly by the upload handler: it runs before the `uploadedImagesURL` state
   * update has settled, so the `mediaURLs` of this render is still the pre-upload list.
   */
  const handleAnalyzeAI = async (urls: string[] = mediaURLs) => {
    const lastUrl = urls[urls.length - 1]
    if (!lastUrl || isReadMode || isReworkLimitReached || isAIProcessing) return

    setIsAIProcessing(true)
    setAiResult(null)

    const subtaskType = isImage ? keys.SUBTASK_TYPES.IMAGE_SUBTASK : keys.SUBTASK_TYPES.VIDEO_SUBTASKS

    try {
      const llmResponseStr = await dispatch(
        callLLMInteraction({
          images: urls,
          reference_images: subtask.reference_images ?? [],
          message_id: subtask.message_id ?? '',
          prompt_id: subtask.prompt_id ?? '',
          message: '',
        })
      ).catch(() => null)

      if (!llmResponseStr) throw new Error('LLM interaction returned no response')

      const parsed = parseLLMResponse(llmResponseStr)
      setAiResult(parsed)

      await dispatch(
        updateResults({
          [isImage ? 'images' : 'videos']: urls,
          tid,
          st_id,
          type: subtaskType,
          resultContextId,
          recurrence_ts,
          status: keys.TASK_STATUSES.REVIEWED_BY_AI.value,
          rating: currentRating,
          reservations: parsed.reservations,
        })
      )

      const mediaHash = getMediaCid({ url: lastUrl, cdn, rid })
      if (mediaHash) {
        const keyForSave = `${commentOid}-${mediaHash}`
        await dispatch(
          createAIComment({
            oid: commentOid,
            message: parsed.feedbackText,
            parentCid: mediaHash,
            rasId: retail_id_context_id,
            objectType: keys.COMMENT_OBJECT_TYPE.COMMENT_ON_IMAGE,
            taskId: tid,
            resultContextId,
            resultLevel,
            keyForSave,
            recurrenceTs: recurrence_ts,
            is_ai: true,
          })
        )
      }

      scrollToBottom()
    } catch (e) {
      console.error('AI analysis failed:', e)
    } finally {
      captureEvent('ai_image_analyzed')
      setIsAIProcessing(false)
    }
  }

  const handleDeleteMediaItem = () => {
    if (isInteractionBlocked) return
    const url = mediaURLs[currentMediaIndex]
    if (!url) return
    const newMedia = mediaURLs.filter((_, index) => index !== currentMediaIndex)
    const mediaHash = getMediaCid({ url, cdn, rid })
    const oid = `${commentOid}-${mediaHash}`

    void deletion.remove({
      persist: () =>
        dispatch(
          updateResults({
            [isImage ? 'images' : 'videos']: newMedia,
            tid,
            st_id,
            type: isImage ? keys.SUBTASK_TYPES.IMAGE_SUBTASK : keys.SUBTASK_TYPES.VIDEO_SUBTASKS,
            resultContextId,
            recurrence_ts,
            status: keys.TASK_STATUSES.INPROGRESS.value,
            rating: currentRating,
            optimistic: false,
          })
        ),
      removeLocal: () => {
        handleDeleteMedia(currentMediaIndex)
        setAiResult(null)
      },
      removeComments: () => mediaHash && dispatch(deleteCommentsOfMedia([oid], [mediaHash], [commentOid])),
    })
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleUploadMedia,
    accept: isImage ? 'image/*' : 'video/*',
  })

  const handleDropBlocked = async () => {}
  const handlePasteBlocked = () => {}

  // ── show flags ──

  const showAIScoreCard =
    !!is_ai_enhanced && mediaURLs.length > 0 && (isAIProcessing || (displayAIScore !== undefined && displayAIFeedback))
  const showAddMoreButton = !is_ai_enhanced && mediaURLs.length > 0 && !isReadMode && !isInteractionBlocked

  useEffect(() => {
    setIsSubtaskBusy?.(loading || isAIProcessing || deletion.busy || isSavingMedia)
    return () => setIsSubtaskBusy?.(false)
  }, [loading, isAIProcessing, deletion.busy, isSavingMedia, setIsSubtaskBusy])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // ── render ──

  return (
    <>
      <style>{shimmerKeyframes}</style>

      <div
        className={splitView ? 'execution-media-layout' : undefined}
        style={splitView ? undefined : { display: 'contents' }}
      >
        <div
          className={splitView ? 'execution-media-visual' : undefined}
          style={splitView ? undefined : { display: 'contents' }}
          dir={isRtl ? 'rtl' : 'ltr'}
        >
          {mediaHeader}
          <Flex
            className={splitView ? 'execution-media-result' : undefined}
            flexDir="column"
            justifyContent="center"
            mt={splitView ? 0 : '30px'}
          >
            <DropzoneWrapper
              className={splitView ? 'execution-media-dropzone' : undefined}
              onDrop={isInteractionBlocked ? handleDropBlocked : onDrop}
              onPaste={isInteractionBlocked ? handlePasteBlocked : handlePaste}
            >
              {uploadFailed && (
                <Text role="alert" color="#c1453f">
                  {t.saveFailed}
                </Text>
              )}
              {mediaURLs.length === 0 ? (
                isReadMode ? (
                  // Read-only viewer (e.g. auditor/watcher / not in audience) with no result yet —
                  // must NOT be able to upload/execute on behalf of the recipient.
                  <EmptyState literalKey={t_no_media} defaultText={t_no_media} py="20px" />
                ) : (
                  // Empty state — no media uploaded yet
                  <Flex flexDir="column" alignItems="center" py="20px" style={{ rowGap: '20px' }}>
                    <Button
                      type="button"
                      variant="icon"
                      aria-label={t.uploadFiles}
                      borderRadius="full"
                      p="20px"
                      m="auto"
                      h="auto"
                      bg="black"
                      onClick={addMediaHandler}
                      cursor={isInteractionBlocked ? 'not-allowed' : 'pointer'}
                      transition="0.2s ease-in-out"
                      _hover={{ bg: 'black', transform: isInteractionBlocked ? 'none' : 'scale(1.1)' }}
                      _focus={{ outline: 'none' }}
                      opacity={isInteractionBlocked ? 0.5 : 1}
                      isDisabled={isInteractionBlocked}
                    >
                      <Icon size={46} color="white" />
                    </Button>
                    <Text color={colors.greyDark} pb="10px" fontWeight="600">
                      Add {isImage ? 'Images' : 'Videos'}
                    </Text>
                    <input
                      type="file"
                      accept={isImage ? 'image/*' : 'video/*'}
                      multiple
                      ref={imageInputRef}
                      style={{ display: 'none' }}
                      onChange={handleUploadMedia as React.ChangeEventHandler<HTMLInputElement>}
                      disabled={isInteractionBlocked}
                    />
                  </Flex>
                )
              ) : (
                // Media present — show slider + overlay controls
                <Flex
                  w="100%"
                  flexDir="column"
                  pos="relative"
                  onMouseEnter={() => setSliderHovered(true)}
                  onMouseLeave={() => setSliderHovered(false)}
                >
                  {deletion.failed && (
                    <Text role="alert" color="#c1453f">
                      {t.saveFailed}
                    </Text>
                  )}
                  {loading && !originalMedia ? (
                    <Skeleton height="300px" w="auto" />
                  ) : (
                    <>
                      <MediaSlider
                        media={mediaURLs}
                        onMediaChange={onMediaChange}
                        currentMediaIndex={currentMediaIndex}
                        fit={splitView ? 'contain' : undefined}
                        height={splitView ? '100%' : undefined}
                      />
                      <MediaOverlayButtons
                        isImage={isImage}
                        isRtl={isRtl}
                        isReadMode={isReadMode}
                        is_ai_enhanced={is_ai_enhanced}
                        isRetakeBlocked={isRetakeBlocked}
                        isInteractionBlocked={isInteractionBlocked}
                        sliderHovered={sliderHovered}
                        mediaURLs={mediaURLs}
                        retakeInputRef={retakeInputRef}
                        handleUploadMedia={handleUploadMedia}
                        t_comment_on_image={t_comment_on_image}
                        t_comment_on_video={t_comment_on_video}
                        t_retake={t_retake}
                        onComment={() => setImageToComment(mediaURLs[currentMediaIndex] ?? mediaURLs[0])}
                        onDelete={handleDeleteMediaItem}
                        deleteLabel={t.delete}
                      />
                    </>
                  )}
                </Flex>
              )}
            </DropzoneWrapper>

            <Flex
              className={splitView ? 'execution-media-add' : undefined}
              flexDir="column"
              my={splitView ? 0 : '20px'}
              alignItems="center"
              style={{ rowGap: '10px' }}
            >
              {isShowSubtaskTitle && (
                <Text mt="10px" fontSize="14px" fontWeight="bold" textAlign="center">
                  {subtask.title}
                </Text>
              )}

              {/* Regular flow: Add more media button */}
              {showAddMoreButton && (
                <Button
                  cursor="pointer"
                  m="auto"
                  h="auto"
                  p={splitView ? '8px 14px' : '20px'}
                  borderRadius={splitView ? '10px' : 'full'}
                  bg="black"
                  display="flex"
                  type="button"
                  variant="icon"
                  aria-label={t.uploadFiles}
                  isDisabled={isInteractionBlocked || loading}
                  alignItems="center"
                  transition="0.2s ease-in-out"
                  _hover={{ transform: 'scale(1.05)' }}
                  _focus={{ outline: 'none' }}
                  onClick={addMediaHandler}
                >
                  <Icon size={splitView ? 18 : 35} color="white" />
                  {splitView && (
                    <Text ml="8px" color="white" fontSize="13px">
                      {isImage ? 'Add images' : 'Add videos'}
                    </Text>
                  )}
                </Button>
              )}

              <input
                type="file"
                accept={isImage ? 'image/*' : 'video/*'}
                multiple
                ref={imageInputRef}
                style={{ display: 'none' }}
                onChange={handleUploadMedia as React.ChangeEventHandler<HTMLInputElement>}
              />
            </Flex>
          </Flex>
          {splitView && <div className="execution-media-actions">{mediaFooter}</div>}
        </div>
        <div
          className={splitView ? 'execution-media-discussion' : undefined}
          style={splitView ? undefined : { display: 'contents' }}
          dir={isRtl ? 'rtl' : 'ltr'}
        >
          {/* AI flow: Score card */}
          {showAIScoreCard && (
            <AIScoreCard
              isAIProcessing={isAIProcessing}
              displayAIScore={displayAIScore}
              aiAvatar={aiAvatar}
              elementsColor={theme?.elementsColor}
              isRtl={isRtl}
            />
          )}

          <ResultCommments
            splitView={splitView}
            subtask={subtask}
            retail_id_context_id={retail_id_context_id}
            recurrence_ts={recurrence_ts}
            resultContextId={resultContextId}
            mediaIndex={currentMediaIndex}
            objectType={keys.COMMENT_OBJECT_TYPE.COMMENT_ON_IMAGE}
            media={mediaURLs}
            tid={tid}
            isMediaLoading={loading || isAIProcessing || deletion.busy || isSavingMedia}
            imageToComment={imageToComment}
            onImageToCommentConsumed={() => setImageToComment(undefined)}
            isAIComment={displayAIScore !== undefined}
            targetParentCommentId={targetParentCommentId}
            targetCommentId={targetCommentId}
          />
          <div ref={bottomRef} />
        </div>
      </div>
    </>
  )
}

export const MediaSubtaskCompleting: React.FC<IMediaSubtaskProps> = (props) =>
  props.splitView ? (
    <ExecutionMediaSubtask {...props} />
  ) : (
    <LegacyMediaSubtaskCompleting
      key={JSON.stringify([
        props.tid,
        props.subtask.st_id,
        props.resultContextId,
        props.recurrence_ts,
        props.mediaType,
      ])}
      {...props}
    />
  )
